import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Threshold in seconds used to detect video loop completion.
 * When mediaTime jumps from near the end (duration - threshold) to near the start (< threshold),
 * we consider one full playthrough complete.
 */
const VIDEO_LOOP_DETECTION_THRESHOLD = 0.5;

interface UseVideoPlaybackTrackingParams {
  videoRef: RefObject<HTMLVideoElement | null>;
  onVideoStart?: () => void;
  onVideoComplete?: () => void;
}

/**
 * Uses requestVideoFrameCallback to track video playback events.
 *
 * The video element must keep loop={true} (hard requirement), which means we cannot rely on
 * the native ended event because it never fires when looping is enabled according to the
 * HTML5 spec, so instead we should use requestVideoFrameCallback, which provides frame-accurate
 * timing and reliable mediaTime metadata, making it a better option than the timeupdate event
 * that fires at irregular intervals.
 *
 * How it works:
 * - video_start: Fires once when the first frame is rendered (mediaTime > 0)
 * - video_complete: Fires once when we detect a loop (mediaTime jumps from near-end to near-start)
 */
export const useVideoPlaybackTracking = ({
  videoRef,
  onVideoStart,
  onVideoComplete,
}: UseVideoPlaybackTrackingParams) => {
  const previousMediaTimeRef = useRef(0);
  const hasTrackedStartRef = useRef(false);
  const hasTrackedCompleteRef = useRef(false);
  const frameCallbackIdRef = useRef(0);

  const handleVideoFrame = useCallback(
    (_now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const { mediaTime } = metadata;
      const { duration } = videoElement;

      // Track video_start: first frame has been rendered
      if (!hasTrackedStartRef.current && mediaTime > 0) {
        hasTrackedStartRef.current = true;
        onVideoStart?.();
      }

      // Track video_complete: detect loop by checking if mediaTime jumped from near-end to near-start
      if (
        !hasTrackedCompleteRef.current &&
        previousMediaTimeRef.current > duration - VIDEO_LOOP_DETECTION_THRESHOLD &&
        mediaTime < VIDEO_LOOP_DETECTION_THRESHOLD
      ) {
        hasTrackedCompleteRef.current = true;
        onVideoComplete?.();
      }

      previousMediaTimeRef.current = mediaTime;

      // Continue tracking frames
      frameCallbackIdRef.current = videoElement.requestVideoFrameCallback(handleVideoFrame);
    },
    [videoRef, onVideoStart, onVideoComplete]
  );

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !('requestVideoFrameCallback' in videoElement)) return;

    frameCallbackIdRef.current = videoElement.requestVideoFrameCallback(handleVideoFrame);

    return () => {
      if ('cancelVideoFrameCallback' in videoElement) {
        videoElement.cancelVideoFrameCallback(frameCallbackIdRef.current);
      }
    };
  }, [videoRef, handleVideoFrame]);
};
